"use client"

import React, { useState, useEffect } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Users, Calendar, Award, Building } from "lucide-react"
import { useTranslations } from "next-intl"


export function AboutStats() {

  const t = useTranslations("page.about");

 
  const stats = [
    {
      icon: Users,
      number: "4,000+",
      label: t("achievements.i1.title"),
      description: t("achievements.i1.description"),
    },
    {
      icon: Calendar,
      number: "20+",
      label: t("achievements.i2.title"),
      description: t("achievements.i2.description"),
    },
    {
      icon: Award,
      number: "50+",
      label: t("achievements.i3.title"),
      description: t("achievements.i3.description"),
    },
    {
      icon: Building,
      number: "3",
      label: t("achievements.i4.title"),
      description: t("achievements.i4.description"),
    },
  ];

  const [currentStatIndex, setCurrentStatIndex] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true)
      setTimeout(() => {
        setCurrentStatIndex((prev) => (prev + 1) % stats.length)
        setIsAnimating(false)
      }, 300) // Half animation duration
    }, 3000) // Rotate every 3 seconds

    return () => clearInterval(interval)
  }, [])

  return (
    <section className="py-20 bg-teal-900 text-white">
      <div className="container px-4 md:px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold sm:text-4xl mb-4">
            {t('achievements.title')}
          </h2>
          <p className="text-xl text-teal-100 max-w-3xl mx-auto">
            {t('achievements.description')}
          </p>
        </div>

        {/* Desktop - All stats visible */}
        <div className="hidden lg:grid gap-8 lg:grid-cols-4">
          {stats.map((stat, index) => {
            const Icon = stat.icon
            return (
              <Card key={index} className="bg-teal-800 border-teal-700">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-4xl font-bold text-white mb-2">{stat.number}</div>
                  <div className="text-lg font-semibold text-teal-100 mb-2">{stat.label}</div>
                  <div className="text-sm text-teal-200">{stat.description}</div>
                </CardContent>
              </Card>
            )
          })}
        </div>

        {/* Mobile & Tablet - Rotating stats */}
        <div className="lg:hidden">
          <div className="relative h-80 overflow-hidden">
            <div 
              className={`absolute inset-0 transition-all duration-600 ease-in-out ${
                isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
              }`}
            >
              <Card className="bg-teal-800 border-teal-700 h-full">
                <CardContent className="p-8 text-center h-full flex flex-col justify-center">
                  <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    {React.createElement(stats[currentStatIndex].icon, { 
                      className: "h-10 w-10 text-white" 
                    })}
                  </div>
                  <div className="text-5xl font-bold text-white mb-4">
                    {stats[currentStatIndex].number}
                  </div>
                  <div className="text-xl font-semibold text-teal-100 mb-3">
                    {stats[currentStatIndex].label}
                  </div>
                  <div className="text-base text-teal-200">
                    {stats[currentStatIndex].description}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Indicators */}
          <div className="flex justify-center mt-6 space-x-2">
            {stats.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStatIndex(index)}
                className={`w-3 h-3 rounded-full transition-colors duration-300 ${
                  index === currentStatIndex 
                    ? 'bg-teal-400' 
                    : 'bg-teal-600 hover:bg-teal-500'
                }`}
                aria-label={`${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
